/**
 * Capillaire PDP — Custom element for the Product Detail Page.
 *
 * Handles: tier selection, subscription toggle, quantity controls,
 * AJAX add-to-cart (with free-gift multi-item), format/variant switching,
 * and image gallery scroll.
 */
function shopifyRoute(path) {
  if (window.LVTheme?.route) return window.LVTheme.route(path);
  const root = window.Shopify?.routes?.root || '/';
  return root + path.replace(/^\/+/, '');
}

class CapillairePdp extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.refs = {};
    this.querySelectorAll('[ref]').forEach((el) => {
      const key = el.getAttribute('ref').replace('[]', '');
      if (el.getAttribute('ref').endsWith('[]')) {
        (this.refs[key] = this.refs[key] || []).push(el);
      } else {
        this.refs[key] = el;
      }
    });

    // Parse tier data
    this.tierData = null;
    if (this.refs.tierData) {
      try {
        this.tierData = JSON.parse(this.refs.tierData.textContent);
      } catch (_) { /* no tiers */ }
    }

    this.selectedTierIndex = 0;
    this.subscriptionActive = false;

    this._bindEvents();
    this._initGallery();
    this._initStickyBar();
  }

  /* ── Sticky add-to-cart bar (mobile, opt-in per template) ──
     Shown once the main add-to-cart button has scrolled above the viewport. The bar's button
     just clicks the main one, so every purchase rule stays in _handleAddToCart. */
  _initStickyBar() {
    const bar = this.querySelector('[data-pdp-sticky]');
    const mainBtn = this.refs.addToCartBtn;
    if (!bar || !mainBtn) return;
    const btn = bar.querySelector('[data-pdp-sticky-btn]');
    const priceEl = bar.querySelector('[data-pdp-sticky-price]');

    const sync = () => {
      btn.textContent = mainBtn.textContent.trim();
      btn.disabled = mainBtn.disabled;
      // copy our own price markup so the strikethrough compare price keeps its styling
      if (priceEl && this.refs.mainPrice) priceEl.innerHTML = this.refs.mainPrice.innerHTML;
    };
    sync();
    new MutationObserver(sync).observe(mainBtn, { childList: true, characterData: true, subtree: true, attributes: true });
    if (this.refs.mainPrice) new MutationObserver(sync).observe(this.refs.mainPrice, { childList: true, characterData: true, subtree: true });

    btn.addEventListener('click', () => mainBtn.click());

    // Scroll-based (not IntersectionObserver): on some product pages the observer's callback never
    // fired, so the bar stayed hidden. Reading the button's position on scroll is simple and reliable.
    let ticking = false;
    const update = () => {
      ticking = false;
      if (!mainBtn.isConnected) return;
      const show = mainBtn.getBoundingClientRect().bottom < 0;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      btn.tabIndex = show ? 0 : -1;
      document.body.classList.toggle('has-pdp-sticky', show);
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  /* ── Event Binding ── */
  _bindEvents() {
    // Tier radios
    (this.refs.tierRadios || []).forEach((radio) => {
      radio.addEventListener('change', () => this._handleTierChange(radio));
    });

    // Subscription checkbox
    if (this.refs.subCheckbox) {
      this.refs.subCheckbox.addEventListener('change', () => this._handleSubscriptionToggle());
    }

    // Quantity
    if (this.refs.qtyMinus) {
      this.refs.qtyMinus.addEventListener('click', () => this._adjustQty(-1));
    }
    if (this.refs.qtyPlus) {
      this.refs.qtyPlus.addEventListener('click', () => this._adjustQty(1));
    }
    if (this.refs.qtyInput) {
      this.refs.qtyInput.addEventListener('change', () => this._normalizeQty());
      this.refs.qtyInput.addEventListener('blur', () => this._normalizeQty());
    }

    // Add to cart
    if (this.refs.addToCartBtn) {
      this.refs.addToCartBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this._handleAddToCart();
      });
    }

    // Format / variant buttons
    (this.refs.formatBtns || []).forEach((btn) => {
      btn.addEventListener('click', () => this._handleFormatChange(btn));
    });

    this._bindCombinedListingButtons();

    // Thumbnail clicks (desktop gallery)
    this.querySelectorAll('.capillaire-pdp__thumb').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const index = parseInt(thumb.dataset.index, 10);
        this._scrollGalleryTo(index);
      });
    });
  }

  /* ── Image Gallery Carousel ── */
  _initGallery() {
    this._galleryTrack = this.refs.galleryTrack;
    if (!this._galleryTrack) return;

    this._slides = this._galleryTrack.querySelectorAll('.capillaire-pdp__slide');
    this._slideCount = this._slides.length;
    this._currentSlide = 0;

    if (this._slideCount < 2) return;

    // Touch / swipe support
    let startX = 0;
    let startY = 0;
    let deltaX = 0;
    let isSwiping = false;

    this._galleryTrack.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      deltaX = 0;
      isSwiping = false;
      this._galleryTrack.style.transition = 'none';
    }, { passive: true });

    this._galleryTrack.addEventListener('touchmove', (e) => {
      deltaX = e.touches[0].clientX - startX;
      const deltaY = e.touches[0].clientY - startY;

      // Only swipe horizontally if horizontal movement > vertical
      if (!isSwiping && Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
        isSwiping = true;
      }

      if (isSwiping) {
        const offset = -(this._currentSlide * 100) + (deltaX / this._galleryTrack.offsetWidth) * 100;
        this._galleryTrack.style.transform = `translateX(${offset}%)`;
      }
    }, { passive: true });

    this._galleryTrack.addEventListener('touchend', () => {
      this._galleryTrack.style.transition = '';
      if (isSwiping) {
        const threshold = this._galleryTrack.offsetWidth * 0.2;
        if (deltaX < -threshold && this._currentSlide < this._slideCount - 1) {
          this._goToSlide(this._currentSlide + 1);
        } else if (deltaX > threshold && this._currentSlide > 0) {
          this._goToSlide(this._currentSlide - 1);
        } else {
          this._goToSlide(this._currentSlide);
        }
      }
    });

    // Dot clicks
    this.querySelectorAll('.capillaire-pdp__dot').forEach((dot) => {
      dot.addEventListener('click', () => {
        this._goToSlide(parseInt(dot.dataset.index, 10));
      });
    });
  }

  _goToSlide(index) {
    if (index < 0) index = 0;
    if (index >= this._slideCount) index = this._slideCount - 1;
    this._currentSlide = index;

    if (this._galleryTrack) {
      this._galleryTrack.style.transform = `translateX(-${index * 100}%)`;
    }

    this._updateGalleryIndicators(index);

    // A video that is no longer on screen must not keep playing
    this._slides.forEach((slide, i) => {
      if (i === index) return;
      slide.querySelectorAll('video').forEach((video) => { if (!video.paused) video.pause(); });
    });
  }

  _scrollGalleryTo(index) {
    this._goToSlide(index);
  }

  _updateGalleryIndicators(activeIndex) {
    // Update thumbnails
    this.querySelectorAll('.capillaire-pdp__thumb').forEach((thumb) => {
      const isActive = parseInt(thumb.dataset.index, 10) === activeIndex;
      thumb.classList.toggle('capillaire-pdp__thumb--active', isActive);
      if (isActive) {
        thumb.setAttribute('aria-current', 'true');
        // Mobile: keep the selected mini photo visible inside the scrolling row (without moving the page)
        const row = thumb.parentElement;
        if (row && row.scrollWidth > row.clientWidth) {
          const left = thumb.offsetLeft - (row.clientWidth - thumb.offsetWidth) / 2;
          row.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
        }
      } else {
        thumb.removeAttribute('aria-current');
      }
    });

    // Update dots (mobile)
    this.querySelectorAll('.capillaire-pdp__dot').forEach((dot) => {
      const isActive = parseInt(dot.dataset.index, 10) === activeIndex;
      dot.classList.toggle('capillaire-pdp__dot--active', isActive);
      if (isActive) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  /* ── Tier Selection ── */
  _handleTierChange(radio) {
    if (radio.disabled) return;
    const previousIndex = this.selectedTierIndex;
    this.selectedTierIndex = parseInt(radio.value, 10);
    const tier = this.tierData?.tiers?.[this.selectedTierIndex];
    if (!tier) return;

    // The subscription discount only ever applies to the currently
    // selected tier — if the row we're leaving was showing a
    // subscription-adjusted compare price (see _updatePriceDisplay),
    // reset it to its plain price now, or it keeps showing a stale
    // "was X, now Y" comparison for a tier that's no longer subscribed.
    // The frequency line gets the same treatment, or a stale phrase from
    // the previous tier lingers under a row that's no longer selected.
    if (previousIndex !== this.selectedTierIndex) {
      const previousTier = this.tierData?.tiers?.[previousIndex];
      const previousRowEl = this.querySelector(`[data-tier-price="${previousIndex}"]`);
      if (previousTier && previousRowEl) {
        previousRowEl.innerHTML = this._formatPriceHTML(previousTier.price, previousTier.compareAtPrice || 0);
      }
      const previousFrequencyEl = this.querySelector(`[data-tier-frequency="${previousIndex}"]`);
      if (previousFrequencyEl) {
        previousFrequencyEl.hidden = true;
      }
    }

    // Update variant ID
    if (this.refs.variantIdInput) {
      this.refs.variantIdInput.value = tier.variantId;
    }

    /* Toggle subscription visibility — this MUST run before
       _updatePriceDisplay(tier) below, not after. A real, confirmed bug:
       switching from a subscribed tier to one with no selling plan left
       `this.subscriptionActive` at its stale `true` value through the
       _updatePriceDisplay(tier) call that used to happen first, so the new
       tier's price briefly (permanently, until the next interaction)
       rendered with the subscription discount and the "Abonnement" badge
       even though it has no selling plan at all. _computeSubscriptionAdjustedPrice
       and _updatePriceDisplay also independently gate on `tier.sellingPlanId`
       now (defense in depth), but resetting subscriptionActive first is
       still correct — the checkbox's own visual state must never lag
       behind the tier it now describes. */
    if (this.refs.subContainer) {
      const hasSub = tier.sellingPlanId != null;
      this.refs.subContainer.hidden = !hasSub;
      const subDiscount = this.querySelector('.capillaire-pdp__sub-discount');
      if (subDiscount && this.tierData?.discount > 0) {
        subDiscount.textContent = `${this.tierData.discount}%`;
      }
      if (!hasSub) {
        this.subscriptionActive = false;
        if (this.refs.subCheckbox) {
          this.refs.subCheckbox.checked = false;
        }
        if (this.refs.sellingPlanInput) {
          this.refs.sellingPlanInput.value = '';
        }
      } else if (this.subscriptionActive && this.refs.sellingPlanInput) {
        this.refs.sellingPlanInput.value = tier.sellingPlanId;
      }
    }

    // Update displayed price
    this._updatePriceDisplay(tier);

    // Update button state
    this._updateButtonState(tier);

    // Update tier row visual state
    this.querySelectorAll('.capillaire-pdp__tier').forEach((row, i) => {
      row.classList.toggle('capillaire-pdp__tier--selected', i === this.selectedTierIndex);
    });

    // Swap gallery image if tier has one, or restore original
    this._updateGalleryImage(tier);

    // Update free gift data attribute
    const giftIds = (tier.freeGiftVariantIds || []).join(',');
    this.dataset.freeGiftVariantIds = giftIds;

    // Update savings display
    this._updateSavingsDisplay(tier);
  }

  _syncTierRows(preferredIndex = this.selectedTierIndex) {
    const tiers = this.tierData?.tiers || [];
    const radios = this.refs.tierRadios || [];

    radios.forEach((radio, index) => {
      const tier = tiers[index];
      const isAvailable = Boolean(tier && tier.available);
      const row = radio.closest('.capillaire-pdp__tier');

      radio.disabled = !isAvailable;
      if (row) {
        row.classList.toggle('capillaire-pdp__tier--disabled', !isAvailable);
        // An upsell tier that is sold out for the selected format is hidden, not greyed out
        // (the product-alone row at index 0 always stays).
        if (index > 0) row.classList.toggle('capillaire-pdp__tier--hidden', !isAvailable);
        if (isAvailable) {
          row.removeAttribute('aria-disabled');
        } else {
          row.setAttribute('aria-disabled', 'true');
        }
      }

      if (!tier) return;

      const priceEl = this.querySelector(`[data-tier-price="${index}"]`);
      if (priceEl) {
        priceEl.innerHTML = this._formatPriceHTML(tier.price, tier.rowCompareAtPrice || 0);
      }

      // No subscription state exists for a freshly-synced format yet (see
      // this method's own opening comment) — every row's frequency line
      // resets to hidden here; _handleTierChange (called right after this,
      // in _handleFormatChange) re-shows it for whichever tier ends up
      // selected and actually has a selling plan.
      const frequencyEl = this.querySelector(`[data-tier-frequency="${index}"]`);
      if (frequencyEl) {
        frequencyEl.hidden = true;
      }

      const detailsEl = this.querySelector(`[data-tier-details="${index}"]`);
      if (detailsEl) {
        detailsEl.textContent = tier.details || '';
        detailsEl.hidden = !tier.details;
      }

      const savingEl = this.querySelector(`[data-tier-saving="${index}"]`);
      if (savingEl) {
        savingEl.textContent = tier.savingText || '';
        savingEl.hidden = !tier.savingText;
      }

      const giftsEl = this.querySelector(`[data-tier-gifts="${index}"]`);
      if (giftsEl) {
        giftsEl.innerHTML = tier.giftHtml || '';
        giftsEl.hidden = !tier.giftHtml;
      }
    });

    // Nothing to choose between when every upsell tier is hidden: hide the whole group.
    const tiersEl = this.querySelector('.capillaire-pdp__tiers');
    if (tiersEl) {
      const hasVisibleUpsell = tiers.some((tier, index) => index > 0 && tier?.available);
      tiersEl.classList.toggle('capillaire-pdp__tiers--solo', !hasVisibleUpsell);
    }

    let nextIndex = Number.isInteger(preferredIndex) ? preferredIndex : 0;
    if (!tiers[nextIndex]?.available) {
      nextIndex = tiers.findIndex((tier) => tier?.available);
    }
    return nextIndex >= 0 ? nextIndex : 0;
  }

  // A server-rendered <picture><source media="(min-width: 48.0625rem)"> on
  // the first slide (see variant-image-data.liquid's as_img mode) always
  // wins over the sibling <img> at desktop widths, no matter what JS sets
  // on the <img> afterward — the browser never re-evaluates it once picked.
  // Any dynamic image swap (format click, tier change) must neutralize that
  // <source> once, or the desktop viewport keeps showing the original,
  // now-stale photo forever while the <img> itself silently updates.
  _neutralizePictureSource(mainImg) {
    const picture = mainImg.closest('picture');
    const source = picture ? picture.querySelector('source') : null;
    if (!source) return;
    if (source.srcset) {
      mainImg.srcset = source.srcset;
      if (source.sizes) mainImg.sizes = source.sizes;
      const firstCandidate = source.srcset.split(',')[0].trim().split(' ')[0];
      if (firstCandidate) mainImg.src = firstCandidate;
    }
    source.remove();
  }

  // Square-image blurred backdrop (see .claude/rules/product-pdp.md "Square
  // Image Support"): keeps the letterbox fill behind a contain-fit square
  // image reflecting that image's own background, on every swap path.
  _updateSlideBg(isSquare, backdropUrl) {
    const bg = this.refs.slideBg;
    if (!bg) return;
    if (isSquare && backdropUrl) {
      bg.style.backgroundImage = `url('${backdropUrl}')`;
      bg.style.display = '';
    } else {
      bg.style.display = 'none';
    }
  }

  _updateGalleryImage(tier) {
    const mainImg = this.querySelector('.capillaire-pdp__slide[data-index="0"] img');
    if (!mainImg) return;
    this._neutralizePictureSource(mainImg);

    // Store original on first call (before any swap)
    if (!this._originalMainSrc) {
      this._originalMainSrc = mainImg.src;
      this._originalMainSrcset = mainImg.srcset || '';
      this._originalMainSizes = mainImg.sizes || '';
      this._originalMainIsSquare = mainImg.classList.contains('capillaire-pdp__slide-img--square');
      this._originalMainBgUrl = this.refs.slideBg?.style.backgroundImage
        ? this.refs.slideBg.style.backgroundImage.replace(/^url\(['"]?/, '').replace(/['"]?\)$/, '')
        : '';
    }

    if (!tier.isSelf && tier.image) {
      if (tier.imageSrcset) {
        mainImg.srcset = tier.imageSrcset;
        if (tier.imageSizes) mainImg.sizes = tier.imageSizes;
      } else {
        mainImg.removeAttribute('srcset');
      }
      mainImg.src = tier.image;
      mainImg.classList.toggle('capillaire-pdp__slide-img--square', !!tier.imageIsSquare);
      this._updateSlideBg(!!tier.imageIsSquare, tier.image);
    } else {
      if (this._originalMainSrcset) {
        mainImg.srcset = this._originalMainSrcset;
      } else {
        mainImg.removeAttribute('srcset');
      }
      if (this._originalMainSizes) {
        mainImg.sizes = this._originalMainSizes;
      } else {
        mainImg.removeAttribute('sizes');
      }
      mainImg.src = this._originalMainSrc;
      mainImg.classList.toggle('capillaire-pdp__slide-img--square', !!this._originalMainIsSquare);
      this._updateSlideBg(!!this._originalMainIsSquare, this._originalMainBgUrl);
    }
  }

  _updateSavingsDisplay(tier) {
    const el = this.refs.savingsDisplay;
    const textEl = this.refs.savingsText;
    if (!el || !textEl) return;

    const saveLabel = this.dataset.saveLabel || '';
    const giftLabel = this.dataset.freeGiftLabel || '';

    let parts = [];

    // Discount savings
    const giftTotal = tier.freeGiftTotal || 0;
    if (!tier.isSelf && tier.compareAtPrice > 0 && tier.compareAtPrice > tier.price) {
      let savings = tier.compareAtPrice - tier.price;
      const savingsIsGiftOnly = giftTotal > 0 && savings === giftTotal && !(tier.discountPercentage > 0);
      if (tier.discountPercentage > 0) {
        parts.push(`${saveLabel} ${tier.discountPercentage}% (${this._formatMoney(savings)})`);
      } else if (!savingsIsGiftOnly) {
        parts.push(`${saveLabel} ${this._formatMoney(savings)}`);
      }
    } else if (!tier.isSelf && tier.discountPercentage > 0) {
      const savings = Math.round(tier.price * tier.discountPercentage / 100);
      parts.push(`${saveLabel} ${tier.discountPercentage}% (${this._formatMoney(savings)})`);
    }

    // Free gift value
    if (giftTotal > 0) {
      parts.push(`+ ${giftLabel} (${this._formatMoney(giftTotal)})`);
    }

    if (parts.length > 0) {
      textEl.textContent = parts.join(' ');
      el.hidden = false;
    } else {
      el.hidden = true;
    }
  }

  _updatePriceDisplay(tier) {
    const { price, comparePrice } = this._computeSubscriptionAdjustedPrice(tier);
    /* Gate on tier.sellingPlanId, not just this.subscriptionActive, for
       both the badge and the frequency line below — this.subscriptionActive
       is a single flag shared across every tier, so on its own it can't
       tell "subscribed" apart from "subscribed to a DIFFERENT tier that
       had a selling plan, before the customer switched to this one." A
       confirmed real bug: switching tiers while subscribed used to leave
       the discount/badge visibly stuck on a tier with no selling plan at
       all until subscriptionActive got reset elsewhere. _handleTierChange
       now resets subscriptionActive before calling this, but that reset
       still can't cover every caller of _updatePriceDisplay (e.g. a future
       one), so checking the tier's own eligibility here too is the actual
       guarantee, not just an optimization. */
    const isSubscribedTier = this.subscriptionActive && tier.sellingPlanId != null;

    /* The "Abonnement" badge only ever belongs on a price that's actually
       showing the subscription-adjusted amount computed just above — it's
       appended here, in _updatePriceDisplay, rather than baked into the
       shared _formatPriceHTML helper, because that helper is also called
       for OTHER tier rows that have nothing to do with the current
       subscription state (the previously-selected row being reset to its
       plain price in _handleTierChange, and every row's base price in
       _syncTierRows on a format swap) — those calls must never gain a
       stray subscription badge. */
    const badgeHtml = isSubscribedTier
      ? ` <span class="capillaire-pdp__price-sub-badge">${this.dataset.subscriptionBadgeLabel || ''}</span>`
      : '';

    const priceEl = this.refs.priceDisplay || this.refs.mainPrice;
    if (priceEl) {
      priceEl.innerHTML = this._formatPriceHTML(price, comparePrice) + badgeHtml;
    }

    if (this.refs.priceFrequency) {
      this.refs.priceFrequency.textContent = tier.sellingPlanName || '';
      this.refs.priceFrequency.hidden = !isSubscribedTier || !tier.sellingPlanName;
    }

    // Keep the selected tier row's own price (e.g. the "SPRAY" self-tier
    // row) in sync with the same subscription comparison shown above —
    // otherwise checking the subscription box only updates the title
    // price, leaving the tier row showing a stale, un-struck-through price.
    const rowPriceEl = this.querySelector(`[data-tier-price="${this.selectedTierIndex}"]`);
    if (rowPriceEl) {
      rowPriceEl.innerHTML = this._formatPriceHTML(price, comparePrice) + badgeHtml;
    }

    // Same frequency phrase, mirrored inside the tier/palier selector row
    // itself, right next to that row's own price.
    const rowFrequencyEl = this.querySelector(`[data-tier-frequency="${this.selectedTierIndex}"]`);
    if (rowFrequencyEl) {
      rowFrequencyEl.textContent = tier.sellingPlanName || '';
      rowFrequencyEl.hidden = !isSubscribedTier || !tier.sellingPlanName;
    }
  }

  // Shared by _updatePriceDisplay (title price + selected tier row) and
  // _syncTierRows would use it too if it ever needed subscription-aware
  // pricing, but _syncTierRows only runs on format swap, before any
  // subscription state exists for the new format — kept separate rather
  // than merged.
  _computeSubscriptionAdjustedPrice(tier) {
    let price = tier.price;
    let comparePrice = tier.compareAtPrice || 0;

    if (this.subscriptionActive && tier.sellingPlanId != null && this.tierData?.discount > 0) {
      const discountPct = tier.discountPercentage || this.tierData.discount;
      const basePrice = tier.price;
      const discountAmount = Math.round(basePrice * discountPct / 100);
      if (comparePrice === 0) comparePrice = basePrice;
      price = basePrice - discountAmount;
    }

    return { price, comparePrice };
  }

  _formatMoney(cents) {
    const amount = (Number(cents) || 0) / 100;
    const currency = this.dataset.currencyCode;
    const locale = this.dataset.locale || document.documentElement.lang || undefined;

    if (currency && window.Intl && Intl.NumberFormat) {
      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
        }).format(amount);
      } catch (_) {
        // Fall through to a simple currency-code format if the browser rejects the locale or currency.
      }
    }

    const formatted = amount.toFixed(2);
    return currency ? `${formatted} ${currency}` : formatted;
  }

  _formatPriceHTML(price, compareAt) {
    if (compareAt && compareAt > price) {
      return `<span class="capillaire-pdp__tier-price--compare">${this._formatMoney(compareAt)}</span> <strong>${this._formatMoney(price)}</strong>`;
    }
    return `<strong>${this._formatMoney(price)}</strong>`;
  }

  _updateButtonState(tier) {
    const btn = this.refs.addToCartBtn;
    if (!btn) return;

    if (tier.available) {
      btn.disabled = false;
      btn.textContent = this.subscriptionActive
        ? this.dataset.subscribeText || btn.textContent
        : this.dataset.addToCartText || btn.textContent;
    } else {
      btn.disabled = true;
      btn.textContent = this.dataset.soldOutText || btn.textContent;
    }
  }

  /* ── Subscription Checkbox ── */
  _handleSubscriptionToggle() {
    const isYes = !!this.refs.subCheckbox?.checked;
    this.subscriptionActive = isYes;

    // Update selling plan input
    if (this.refs.sellingPlanInput) {
      const tier = this.tierData?.tiers?.[this.selectedTierIndex];
      this.refs.sellingPlanInput.value = isYes && tier?.sellingPlanId
        ? tier.sellingPlanId
        : '';
    }

    // Recalculate price
    const tier = this.tierData?.tiers?.[this.selectedTierIndex];
    if (tier) {
      this._updatePriceDisplay(tier);
      this._updateButtonState(tier);
      this._updateSavingsDisplay(tier);
    }
  }

  /* ── Quantity ── */
  _adjustQty(delta) {
    const input = this.refs.qtyInput;
    if (!input) return;
    const current = this._normalizeQty();
    const next = Math.max(1, Math.min(99, current + delta));
    input.value = next;
  }

  _normalizeQty() {
    const input = this.refs.qtyInput;
    if (!input) return 1;
    const current = parseInt(input.value, 10);
    const next = Math.max(1, Math.min(99, Number.isNaN(current) ? 1 : current));
    input.value = next;
    return next;
  }

  /* ── Format / Variant Switch ── */
  _handleFormatChange(btn) {
    if (btn.disabled) return;

    // A merged combined-listing product can still have a format whose true
    // source product differs from the URL currently being viewed (see
    // .claude/rules/product-pdp.md "Cross-Product Combined Listings"). When
    // that's the case, defer to the same fetch-and-swap flow used for
    // single-variant cross-product listings instead of a same-page ?variant=
    // swap.
    const connectedUrl = btn.dataset.connectedProductUrl;
    if (connectedUrl && connectedUrl !== window.location.pathname) {
      this._handleCombinedListingFormatChange(btn);
      return;
    }

    const variantId = btn.dataset.variantId;
    if (!variantId) return;
    const price = parseInt(btn.dataset.price, 10);
    const comparePrice = btn.dataset.comparePrice ? parseInt(btn.dataset.comparePrice, 10) : 0;
    const sellingPlanId = btn.dataset.sellingPlanId || null;
    const sellingPlanDiscount = parseInt(btn.dataset.sellingPlanDiscount, 10);

    // Update active state
    (this.refs.formatBtns || []).forEach((b) => {
      const isActive = b === btn;
      b.classList.toggle('capillaire-pdp__format-btn--active', isActive);
      b.setAttribute('aria-checked', isActive ? 'true' : 'false');
    });

    if (!Number.isNaN(sellingPlanDiscount) && this.tierData) {
      this.tierData.discount = sellingPlanDiscount;
    }

    // Cache this format's own image (combined-listing-aware) as the "self" baseline
    // so _updateGalleryImage's self-tier revert path shows the right photo instead
    // of whatever image happened to be on the page at first load. Prefer the wide
    // desktop/landscape image (grid.featured_image[1]) since the gallery is a wide
    // column, falling back to the square candidate only if no landscape image exists.
    const formatImage = btn.dataset.variantDesktopImage || btn.dataset.variantImage;
    if (formatImage) {
      this._originalMainSrc = formatImage;
      this._originalMainSrcset = btn.dataset.variantDesktopImageSrcset || btn.dataset.variantImageSrcset || '';
      this._originalMainSizes = btn.dataset.variantDesktopImageSizes || btn.dataset.variantImageSizes || '';
      this._originalMainIsSquare = btn.dataset.variantImageSquare === 'true';
      this._originalMainBgUrl = formatImage;
    }

    let nextTierIndex = this.selectedTierIndex;
    const variantTierSet = this.tierData?.tiersByVariant?.[String(variantId)];

    if (variantTierSet) {
      this.tierData.tiers = variantTierSet;
      nextTierIndex = this._syncTierRows(nextTierIndex);
    } else if (this.tierData?.tiers?.[0]) {
      this.tierData.tiers[0].price = price;
      this.tierData.tiers[0].variantId = parseInt(variantId, 10);
      this.tierData.tiers[0].available = btn.dataset.variantAvailable === 'true';
      this.tierData.tiers[0].sellingPlanId = sellingPlanId;
      this.tierData.tiers[0].compareAtPrice = comparePrice;
      nextTierIndex = this._syncTierRows(0);
    }

    const tierRadio = this.refs.tierRadios?.[nextTierIndex];
    if (tierRadio) {
      tierRadio.checked = true;
      this._handleTierChange(tierRadio);
    } else {
      if (this.refs.variantIdInput) {
        this.refs.variantIdInput.value = variantId;
      }
      if (this.refs.mainPrice) {
        this.refs.mainPrice.innerHTML = this._formatPriceHTML(price, comparePrice);
      }
      // No tier row exists for this product, so _updateGalleryImage never runs —
      // apply the format's own image directly.
      if (formatImage) {
        const mainImg = this.querySelector('.capillaire-pdp__slide[data-index="0"] img');
        if (mainImg) {
          this._neutralizePictureSource(mainImg);
          mainImg.src = formatImage;
          if (this._originalMainSrcset) {
            mainImg.srcset = this._originalMainSrcset;
            if (this._originalMainSizes) mainImg.sizes = this._originalMainSizes;
          } else {
            mainImg.removeAttribute('srcset');
          }
          mainImg.classList.toggle('capillaire-pdp__slide-img--square', !!this._originalMainIsSquare);
          this._updateSlideBg(!!this._originalMainIsSquare, this._originalMainBgUrl);
        }
      }
    }

    // Update the URL with variant
    const url = new URL(window.location);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url);
  }

  /* ── Combined Listings (cross-product) ── */
  _bindCombinedListingButtons() {
    (this.refs.combinedFormatBtns || []).forEach((btn) => {
      btn.addEventListener('click', () => this._handleCombinedListingFormatChange(btn));
    });
  }

  async _handleCombinedListingFormatChange(btn) {
    if (btn.classList.contains('capillaire-pdp__format-btn--active')) return;

    const connectedUrl = btn.dataset.connectedProductUrl;
    if (!connectedUrl) return;

    const mainEl = document.getElementById('main-content');
    if (!mainEl) return;

    // Computed from the DOM rather than a fixed ref array, since this method
    // is shared by both the single-variant cross-product picker
    // (combinedFormatBtns[]) and the merged-listing pdp-format picker
    // (formatBtns[]) -- see _handleFormatChange's early-return guard.
    const siblingBtns = btn.closest('.capillaire-pdp__format-options')?.querySelectorAll('.capillaire-pdp__format-btn') || [btn];

    siblingBtns.forEach((b) => {
      b.disabled = true;
    });
    mainEl.classList.add('is-swapping-product');

    if (this._combinedListingAbort) this._combinedListingAbort.abort();
    this._combinedListingAbort = new AbortController();

    try {
      const response = await fetch(connectedUrl, { signal: this._combinedListingAbort.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const responseText = await response.text();
      const parsed = new DOMParser().parseFromString(responseText, 'text/html');
      const newMain = parsed.getElementById('main-content');
      if (!newMain) throw new Error('No #main-content found in fetched page');

      mainEl.innerHTML = newMain.innerHTML;
      mainEl.classList.remove('is-swapping-product');
      if (parsed.title) document.title = parsed.title;

      const newUrl = new URL(connectedUrl, window.location.origin);
      history.replaceState({}, '', newUrl.toString());

      window.LVTheme?.announce?.(parsed.title || '');
    } catch (err) {
      if (err.name === 'AbortError') return;

      siblingBtns.forEach((b) => {
        b.disabled = false;
      });
      mainEl.classList.remove('is-swapping-product');

      if (this.refs.addToCartError) {
        this.refs.addToCartError.textContent = this.dataset.cartErrorText || '';
        this.refs.addToCartError.hidden = false;
        setTimeout(() => {
          this.refs.addToCartError.hidden = true;
        }, 4000);
      }
    }
  }

  /* ── Add to Cart ── */
  async _handleAddToCart() {
    const btn = this.refs.addToCartBtn;
    if (!btn || btn.disabled || btn.classList.contains('is-loading')) return;

    const variantId = this.refs.variantIdInput?.value;
    const qty = this._normalizeQty();
    const sellingPlan = this.refs.sellingPlanInput?.value || '';

    if (!variantId) return;

    // Resolve current tier data
    const tier = this.tierData?.tiers?.[this.selectedTierIndex];

    // Build items array
    const items = [];
    const quantityMultiplier = tier && !tier.isSelf
      ? Math.max(1, parseInt(tier.quantityMultiplier || 1, 10))
      : 1;
    const giftQuantityMultiplier = tier && !tier.isSelf
      ? Math.max(1, parseInt(tier.giftQuantityMultiplier || 1, 10))
      : 1;

    const mainItem = { id: parseInt(variantId, 10), quantity: qty * quantityMultiplier };
    if (sellingPlan) mainItem.selling_plan = parseInt(sellingPlan, 10);

    // Add cart properties for tier products (duo, trio, coffret)
    // so the Shopify Function can apply the correct discount
    if (tier && !tier.isSelf) {
      mainItem.properties = {
        '_bundle': 'true',
        '_discount_pct': String(tier.discountPercentage || 0)
      };
      if (tier.compareAtPrice > 0) {
        mainItem.properties['_compare_at'] = String(tier.compareAtPrice);
      }
    }

    items.push(mainItem);

    // Add free gift variants with properties for discount script.
    // `data-free-gift-variant-ids` is the SELF product's own configured gift
    // (self-tier gifts are never encoded per-tier -- pdp-tier-json.liquid
    // hardcodes the self tier's own freeGiftVariantIds to [] by design), so
    // it must only be used when there's no distinct non-self tier selected.
    // A defined non-self tier's own (possibly empty) freeGiftVariantIds is
    // always authoritative -- falling back to the dataset attribute whenever
    // that array happened to be empty (the old `.length ? ... : ...` check)
    // silently attached the SELF product's unrelated gift onto a bundle/
    // coffret tier that has no gift of its own, doubling the cart line for
    // that gift's variant whenever it coincided with the tier's own product.
    // Confirmed real case: selecting a giftless coffret tier still added a
    // second line, sourced from the base product's own lv_free_gift.
    const giftIds = (tier && !tier.isSelf)
      ? (tier.freeGiftVariantIds || [])
      : (this.dataset.freeGiftVariantIds || '').split(',').filter(Boolean);

    giftIds.forEach((giftId) => {
      items.push({
        id: parseInt(giftId, 10),
        quantity: qty * giftQuantityMultiplier,
        properties: {
          '_free_gift': 'true',
          '_gift_with': String(variantId)
        }
      });
    });

    // Set loading state
    btn.classList.add('is-loading');
    const originalText = btn.textContent;
    btn.textContent = this.dataset.addingText || originalText;
    window.LVTheme?.announce?.(this.dataset.addingText || originalText);

    try {
      if (window.LVTheme?.cart) {
        await window.LVTheme.cart.addItems(items, { refreshDrawer: true });
      } else {
        const res = await fetch(shopifyRoute('cart/add.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items }),
        });

        if (!res.ok) {
          let message = this.dataset.cartErrorText || '';
          try {
            const data = await res.json();
            message = data.description || data.message || message;
          } catch (_) { /* Keep translated fallback. */ }
          throw new Error(message);
        }
      }

      // Success
      btn.classList.remove('is-loading');
      btn.classList.add('is-added');
      btn.textContent = this.dataset.addedText || originalText;
      window.LVTheme?.announce?.(this.dataset.addedText || originalText);

      // Refresh and open cart drawer
      if (window.cartDrawer) {
        if (!window.LVTheme?.cart && window.cartDrawer.refresh) {
          await window.cartDrawer.refresh();
        }
        window.cartDrawer.open();
      }

      setTimeout(() => {
        btn.classList.remove('is-added');
        btn.textContent = originalText;
      }, 1500);
    } catch (err) {
      btn.classList.remove('is-loading');
      btn.textContent = originalText;

      // Show error
      if (this.refs.addToCartError) {
        this.refs.addToCartError.textContent = err.message;
        this.refs.addToCartError.hidden = false;
        window.LVTheme?.announce?.(err.message);
        setTimeout(() => {
          this.refs.addToCartError.hidden = true;
        }, 4000);
      }
    }
  }
}

if (!customElements.get('capillaire-pdp')) {
  customElements.define('capillaire-pdp', CapillairePdp);
}
